/**
 * Dashboard API client — spec-09 unified portal
 *
 * Provides typed fetch functions + TanStack Query hooks for all dashboard
 * widgets. Each hook gracefully falls back to sample data when the backend
 * endpoint is not yet reachable (service unavailable / 404 / network error).
 */

import { useQuery } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function tenantFromJwt(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return claims.tenant_id || claims.tenant || null;
  } catch { return null; }
}

function getAuthHeaders(): HeadersInit {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const token = localStorage.getItem('access_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const tenant = localStorage.getItem('tenant_id') || (token ? tenantFromJwt(token) : null);
    if (tenant) headers['X-Tenant-ID'] = tenant;
  } catch { /* SSR safety */ }
  return headers;
}

// Sentinel returned when a backend endpoint is unavailable (404/422/5xx/network error).
// Consumers must check `data?.__unavailable` and render an empty/coming-soon state.
export interface UnavailableData {
  __unavailable: true;
  status: number | 'network_error';
  url: string;
}

// Use fixtures only when explicitly opted-in via env var (dev-only convenience).
const USE_FIXTURES = import.meta.env.DEV && import.meta.env.VITE_DASHBOARD_USE_FIXTURES === 'true';

async function apiFetch<T>(url: string, fallback: T): Promise<T | UnavailableData> {
  try {
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) {
      if (USE_FIXTURES) return fallback;
      return { __unavailable: true, status: res.status, url } as UnavailableData;
    }
    return (await res.json()) as T;
  } catch {
    if (USE_FIXTURES) return fallback;
    return { __unavailable: true, status: 'network_error', url } as UnavailableData;
  }
}

export function isUnavailable(data: unknown): data is UnavailableData {
  return !!data && typeof data === 'object' && '__unavailable' in data && (data as UnavailableData).__unavailable === true;
}

// ---------------------------------------------------------------------------
// Shared lookup: companies (for client_company_id → display name resolution)
// ---------------------------------------------------------------------------

interface CompanyApiRow {
  id: string;
  name: string;
}

let _companiesCache: { ts: number; map: Map<string, string> } | null = null;
const COMPANIES_CACHE_TTL_MS = 60_000;

export async function getCompaniesMap(): Promise<Map<string, string>> {
  const now = Date.now();
  if (_companiesCache && now - _companiesCache.ts < COMPANIES_CACHE_TTL_MS) {
    return _companiesCache.map;
  }
  try {
    // Backend caps `limit` at 200 (Pydantic Query(le=200)); 500 returned 422 and
    // wiped the cache, causing client-name fallbacks to render `—` on dashboard
    // widgets + /compliance/states (spec-09 QA audit Bug A/B, 2026-04-27).
    const res = await fetch('/api/v1/companies?limit=200', { headers: getAuthHeaders() });
    if (!res.ok) return new Map();
    const json = await res.json();
    const items: CompanyApiRow[] = Array.isArray(json) ? json : (json.items ?? []);
    const map = new Map<string, string>();
    for (const c of items) {
      if (c?.id && c?.name) map.set(c.id, c.name);
    }
    _companiesCache = { ts: now, map };
    return map;
  } catch {
    return new Map();
  }
}

// ---------------------------------------------------------------------------
// Shared lookup: retainer contracts (for contract_id → contract_code + client_company_id)
// ---------------------------------------------------------------------------

interface RetainerContractApiRow {
  id: string;
  contract_code: string;
  client_company_id: string;
  status: string;
}

let _contractsCache: { ts: number; map: Map<string, RetainerContractApiRow> } | null = null;
const CONTRACTS_CACHE_TTL_MS = 60_000;

async function getContractsMap(): Promise<Map<string, RetainerContractApiRow>> {
  const now = Date.now();
  if (_contractsCache && now - _contractsCache.ts < CONTRACTS_CACHE_TTL_MS) {
    return _contractsCache.map;
  }
  try {
    // Backend caps `limit` at 200 (Pydantic Query(le=200)); 500 returned 422 and
    // wiped the cache, causing the Upcoming Renewals widget to fall back to the
    // `…<last6>` UUID fragment (spec-09 QA audit Bug B, 2026-04-27).
    const res = await fetch('/api/v1/retainer-contracts?limit=200', { headers: getAuthHeaders() });
    if (!res.ok) return new Map();
    const json = await res.json();
    const items: RetainerContractApiRow[] = Array.isArray(json) ? json : (json.items ?? []);
    const map = new Map<string, RetainerContractApiRow>();
    for (const c of items) {
      if (c?.id) map.set(c.id, c);
    }
    _contractsCache = { ts: now, map };
    return map;
  } catch {
    return new Map();
  }
}

// ---------------------------------------------------------------------------
// Retainer portfolio burn-down
// ---------------------------------------------------------------------------

export interface BurnDownEntry {
  id: string;
  contract_code: string;
  client_name: string;
  contract_hours: number;
  hours_used: number;
  balance: number;
  pct_consumed: number;
  threshold: 'green' | 'amber' | 'red' | 'darkred';
  status: string;
}

const SAMPLE_BURNDOWN: BurnDownEntry[] = [
  { id: '1', contract_code: 'RC-2026-001', client_name: 'Alutec Gulf Ltd', contract_hours: 100, hours_used: 45, balance: 55, pct_consumed: 45, threshold: 'green', status: 'active' },
  { id: '2', contract_code: 'RC-2026-002', client_name: 'Jubaili Bros', contract_hours: 80, hours_used: 72, balance: 8, pct_consumed: 90, threshold: 'red', status: 'active' },
  { id: '3', contract_code: 'RC-2026-003', client_name: 'Riyadh Holdings', contract_hours: 60, hours_used: 50, balance: 10, pct_consumed: 83, threshold: 'amber', status: 'active' },
  { id: '4', contract_code: 'RC-2026-004', client_name: 'AlexCo Trading', contract_hours: 40, hours_used: 15, balance: 25, pct_consumed: 37, threshold: 'green', status: 'active' },
];

interface BurnDownApiRow {
  contract_id: string;
  contract_code: string;
  client_company_id: string;
  contract_hours: number;
  hours_used: number;
  balance: number;
  pct_consumed: number;
  threshold: string | null;
  period_end: string | null;
  days_to_renewal: number | null;
}

function mapThreshold(pct: number, raw: string | null): BurnDownEntry['threshold'] {
  if (raw === 'overage' || pct >= 100) return 'darkred';
  if (pct >= 90) return 'red';
  if (pct >= 75) return 'amber';
  return 'green';
}

async function fetchPortfolioBurnDown(): Promise<BurnDownEntry[] | UnavailableData> {
  try {
    const res = await fetch('/api/v1/retainers/portfolio-burn-down', { headers: getAuthHeaders() });
    if (!res.ok) {
      if (USE_FIXTURES) return SAMPLE_BURNDOWN;
      return { __unavailable: true, status: res.status, url: '/api/v1/retainers/portfolio-burn-down' } as UnavailableData;
    }
    const json = await res.json();
    const items: (BurnDownApiRow & { client_company_id?: string })[] = Array.isArray(json) ? json : (json.items ?? []);
    // Bug A/B fix — payload returns only contract_id + client_company_id; resolve names via batch fetch.
    const [contractsMap, companiesMap] = await Promise.all([getContractsMap(), getCompaniesMap()]);
    return items.map((r) => {
      const contract = contractsMap.get(r.contract_id);
      const companyId = r.client_company_id ?? contract?.client_company_id;
      const clientName = (companyId && companiesMap.get(companyId)) || r.contract_code;
      return {
        id: r.contract_id,
        contract_code: r.contract_code,
        client_name: clientName,
        contract_hours: r.contract_hours,
        hours_used: r.hours_used,
        balance: r.balance,
        pct_consumed: r.pct_consumed,
        threshold: mapThreshold(r.pct_consumed, r.threshold),
        // Bug D fix — normalize to lowercase so widget i18n lookup `status.active` matches
        // regardless of whether backend returns `active`, `Active`, or `ACTIVE`.
        status: ((contract?.status ?? 'active') as string).toLowerCase(),
      };
    });
  } catch {
    if (USE_FIXTURES) return SAMPLE_BURNDOWN;
    return { __unavailable: true, status: 'network_error', url: '/api/v1/retainers/portfolio-burn-down' } as UnavailableData;
  }
}

export function usePortfolioBurnDown() {
  return useQuery({
    queryKey: ['dashboard', 'portfolio-burn-down'],
    queryFn: fetchPortfolioBurnDown,
    staleTime: 60_000,
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// Upcoming renewals
// ---------------------------------------------------------------------------

export interface UpcomingRenewal {
  id: string;
  contract_id?: string;
  contract_code: string;
  client_name: string;
  client_display_name?: string;
  due_date: string;
  status: string;
  days_until: number;
}

const SAMPLE_RENEWALS: UpcomingRenewal[] = [
  { id: '1', contract_code: 'RC-2026-001', client_name: 'Alutec Gulf Ltd', due_date: '2026-05-10', status: 'pending', days_until: 14 },
  { id: '2', contract_code: 'RC-2026-003', client_name: 'Riyadh Holdings', due_date: '2026-06-01', status: 'notified', days_until: 36 },
  { id: '3', contract_code: 'RC-2025-009', client_name: 'Gulf Steel Corp', due_date: '2026-07-15', status: 'pending', days_until: 80 },
];

interface RenewalApiRow {
  id: string;
  contract_id: string;
  due_date: string;
  status: string;
}

async function fetchUpcomingRenewals(days: number): Promise<UpcomingRenewal[] | UnavailableData> {
  try {
    const res = await fetch(`/api/v1/retainers/renewals/upcoming?days=${days}`, { headers: getAuthHeaders() });
    if (!res.ok) {
      if (USE_FIXTURES) return SAMPLE_RENEWALS.filter(r => r.days_until <= days);
      return { __unavailable: true, status: res.status, url: `/api/v1/retainers/renewals/upcoming?days=${days}` } as UnavailableData;
    }
    const json = await res.json();
    const items: RenewalApiRow[] = Array.isArray(json) ? json : (json.items ?? []);
    // Bug A fix — backend returns only {id, contract_id, due_date, status}; resolve display labels
    // by batch-fetching contracts (for contract_code) + companies (for client_name).
    const [contractsMap, companiesMap] = await Promise.all([getContractsMap(), getCompaniesMap()]);
    const today = Date.now();
    return items.map((r) => {
      const due = new Date(r.due_date).getTime();
      const days_until = Math.round((due - today) / 86400000);
      const contract = contractsMap.get(r.contract_id);
      const contractCode = contract?.contract_code ?? `…${r.contract_id.slice(-6)}`;
      const clientName = (contract?.client_company_id && companiesMap.get(contract.client_company_id))
        || '—';
      return {
        id: r.id,
        contract_id: r.contract_id,
        contract_code: contractCode,
        client_name: clientName,
        due_date: r.due_date,
        // Bug D fix — normalize to lowercase to ensure i18n status mapper hits.
        status: (r.status ?? '').toLowerCase(),
        days_until,
      };
    });
  } catch {
    if (USE_FIXTURES) return SAMPLE_RENEWALS.filter(r => r.days_until <= days);
    return { __unavailable: true, status: 'network_error', url: `/api/v1/retainers/renewals/upcoming?days=${days}` } as UnavailableData;
  }
}

export function useUpcomingRenewals(days: 30 | 60 | 90) {
  return useQuery({
    queryKey: ['dashboard', 'upcoming-renewals', days],
    queryFn: () => fetchUpcomingRenewals(days),
    staleTime: 300_000,
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// SLA breaches
// ---------------------------------------------------------------------------

export interface SlaBreachEmail {
  id: string;
  subject: string;
  from_address: string;
  received_at: string;
  sla_due_at: string;
  breached_at: string;
}

const SAMPLE_SLA_BREACHES: SlaBreachEmail[] = [
  { id: '1', subject: 'Urgent: CR renewal overdue', from_address: 'ops@alutec.com', received_at: '2026-04-20T09:00:00Z', sla_due_at: '2026-04-21T09:00:00Z', breached_at: '2026-04-21T09:01:00Z' },
  { id: '2', subject: 'POA signing request', from_address: 'legal@jubaili.com', received_at: '2026-04-19T14:00:00Z', sla_due_at: '2026-04-20T14:00:00Z', breached_at: '2026-04-20T14:05:00Z' },
];

async function fetchSlaBreaches(): Promise<SlaBreachEmail[] | UnavailableData> {
  // No purpose-built /sla-breaches endpoint; derive from inbound-emails:
  // anything received >24h ago that is still in `classified` (i.e. nobody
  // acted on it) counts as an SLA breach. This matches the BRD intent
  // (SLA breach = email past response window without action).
  type Email = {
    id: string;
    subject?: string;
    from_address?: string;
    received_at?: string;
    ingested_at?: string;
    processing_status?: string;
  };
  const raw = await apiFetch<Email[]>(
    '/api/v1/email-intake/inbound-emails?limit=200',
    SAMPLE_SLA_BREACHES as unknown as Email[],
  );
  if (isUnavailable(raw)) return raw;
  const cutoffMs = Date.now() - 24 * 60 * 60 * 1000;
  return raw
    .filter((e) => {
      if (e.processing_status !== 'classified') return false;
      const received = e.received_at ?? e.ingested_at;
      if (!received) return false;
      return new Date(received).getTime() < cutoffMs;
    })
    .sort((a, b) => {
      const ta = new Date(a.received_at ?? a.ingested_at ?? 0).getTime();
      const tb = new Date(b.received_at ?? b.ingested_at ?? 0).getTime();
      return ta - tb;
    })
    .slice(0, 10)
    .map((e) => {
      const received = e.received_at ?? e.ingested_at ?? '';
      const sla_due = received
        ? new Date(new Date(received).getTime() + 24 * 60 * 60 * 1000).toISOString()
        : '';
      return {
        id: e.id,
        subject: e.subject ?? '',
        from_address: e.from_address ?? '',
        received_at: received,
        sla_due_at: sla_due,
        breached_at: sla_due,
      };
    });
}

export function useSlaBreaches() {
  return useQuery({
    queryKey: ['dashboard', 'sla-breaches'],
    queryFn: fetchSlaBreaches,
    staleTime: 30_000,
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// Email inbox summary
// ---------------------------------------------------------------------------

export interface EmailSummaryItem {
  id: string;
  subject: string;
  from_address: string;
  received_at: string;
  status: string;
}

const SAMPLE_EMAIL_SUMMARY: EmailSummaryItem[] = [
  { id: '1', subject: 'New company incorporation request', from_address: 'client@alexco.com', received_at: '2026-04-26T08:00:00Z', status: 'proposed' },
  { id: '2', subject: 'Retainer renewal confirmation', from_address: 'ceo@riyadhholdings.com', received_at: '2026-04-26T07:30:00Z', status: 'proposed' },
  { id: '3', subject: 'Gov fee receipt attached', from_address: 'accounting@gulfsteel.com', received_at: '2026-04-25T16:00:00Z', status: 'proposed' },
];

async function fetchEmailInboxSummary(): Promise<EmailSummaryItem[] | UnavailableData> {
  // email-intake-service serves entities at /api/v1/inbound-emails; the Vite
  // proxy strips /email-intake to keep the portal call semantically scoped.
  const raw = await apiFetch<Array<Record<string, unknown>>>(
    '/api/v1/email-intake/inbound-emails?limit=10',
    SAMPLE_EMAIL_SUMMARY as unknown as Array<Record<string, unknown>>,
  );
  if (isUnavailable(raw)) return raw;
  return raw.map((row) => ({
    id: String(row.id ?? ''),
    subject: String(row.subject ?? ''),
    from_address: String(row.from_address ?? ''),
    received_at: String(row.received_at ?? row.ingested_at ?? ''),
    status: String(row.processing_status ?? ''),
  }));
}

export function useEmailInboxSummary() {
  return useQuery({
    queryKey: ['dashboard', 'email-inbox-summary'],
    queryFn: fetchEmailInboxSummary,
    staleTime: 30_000,
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// Setup kanban preview
// ---------------------------------------------------------------------------

export interface SetupMatterPreview {
  id: string;
  client_name: string;
  tx_type_code: string;
  current_phase: string;
  status: string;
}

const SAMPLE_SETUP_MATTERS: SetupMatterPreview[] = [
  { id: '1', client_name: 'Alutec Gulf Ltd', tx_type_code: 'Post_Incorporation', current_phase: 'CR', status: 'under_process' },
  { id: '2', client_name: 'NewCo LLC', tx_type_code: 'Pre_Incorporation', current_phase: 'Name_Reservation', status: 'under_process' },
  { id: '3', client_name: 'Gulf Steel Corp', tx_type_code: 'Post_Incorporation', current_phase: 'Bank_Account', status: 'under_process' },
];

async function fetchSetupKanbanPreview(): Promise<SetupMatterPreview[] | UnavailableData> {
  return apiFetch('/api/v1/setup/matters?status=under_process&limit=20', SAMPLE_SETUP_MATTERS);
}

export function useSetupKanbanPreview() {
  return useQuery({
    queryKey: ['dashboard', 'setup-kanban-preview'],
    queryFn: fetchSetupKanbanPreview,
    staleTime: 60_000,
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// Deal funnel
// ---------------------------------------------------------------------------

export interface DealFunnelStage {
  stage: string;
  count: number;
  total_value: number;
}

const SAMPLE_DEAL_FUNNEL: DealFunnelStage[] = [
  { stage: 'lead', count: 8, total_value: 240000 },
  { stage: 'proposal', count: 5, total_value: 180000 },
  { stage: 'negotiation', count: 3, total_value: 120000 },
  { stage: 'closed_won', count: 2, total_value: 90000 },
  { stage: 'closed_lost', count: 1, total_value: 30000 },
];

async function fetchDealFunnel(): Promise<DealFunnelStage[] | UnavailableData> {
  // /deals/pipeline-summary requires tenant_id and wraps stages in {stages:[...]}
  const tenantId = localStorage.getItem('tenant_id') ?? '';
  const raw = await apiFetch<{ stages: Array<Record<string, unknown>> }>(
    `/api/v1/deals/pipeline-summary?tenant_id=${encodeURIComponent(tenantId)}`,
    { stages: SAMPLE_DEAL_FUNNEL as unknown as Array<Record<string, unknown>> },
  );
  if (isUnavailable(raw)) return raw;
  const stages = Array.isArray((raw as { stages?: unknown }).stages)
    ? ((raw as { stages: Array<Record<string, unknown>> }).stages)
    : [];
  return stages.map((s) => ({
    stage: String(s.code ?? s.stage ?? ''),
    count: Number(s.count ?? 0),
    total_value: Number(s.expected_revenue ?? s.total_value ?? 0),
  }));
}

export function useDealFunnel() {
  return useQuery({
    queryKey: ['dashboard', 'deal-funnel'],
    queryFn: fetchDealFunnel,
    staleTime: 300_000,
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// Points leaderboard
// ---------------------------------------------------------------------------

export interface LeaderboardEntry {
  rank: number;
  lawyer_id: string;
  lawyer_name: string;
  points: number;
}

const SAMPLE_LEADERBOARD: LeaderboardEntry[] = [
  { rank: 1, lawyer_id: 'l1', lawyer_name: 'Ahmad Al-Rashid', points: 1250 },
  { rank: 2, lawyer_id: 'l2', lawyer_name: 'Sara Al-Otaibi', points: 1100 },
  { rank: 3, lawyer_id: 'l3', lawyer_name: 'Omar Khalil', points: 980 },
  { rank: 4, lawyer_id: 'l4', lawyer_name: 'Layla Hassan', points: 870 },
  { rank: 5, lawyer_id: 'l5', lawyer_name: 'Khalid Bin Nasser', points: 750 },
];

async function fetchPointsLeaderboard(): Promise<LeaderboardEntry[] | UnavailableData> {
  // No purpose-built leaderboard endpoint; synthesize from /api/v1/lawyers
  // (which carries points_ytd as a numeric string from the ORM).
  type LawyerRow = {
    user_id?: string;
    id?: string;
    display_name?: string;
    display_name_ar?: string;
    points_ytd?: string | number;
    active?: boolean;
  };
  const raw = await apiFetch<LawyerRow[]>(
    '/api/v1/lawyers?limit=50',
    SAMPLE_LEADERBOARD as unknown as LawyerRow[],
  );
  if (isUnavailable(raw)) return raw;
  return raw
    .filter((l) => l.active !== false)
    .map((l) => ({
      lawyer_id: String(l.user_id ?? l.id ?? ''),
      lawyer_name: String(l.display_name ?? l.display_name_ar ?? ''),
      points: Number(l.points_ytd ?? 0),
    }))
    .sort((a, b) => b.points - a.points)
    .slice(0, 5)
    .map((row, idx) => ({ ...row, rank: idx + 1 }));
}

export function usePointsLeaderboard() {
  return useQuery({
    queryKey: ['dashboard', 'points-leaderboard'],
    queryFn: fetchPointsLeaderboard,
    staleTime: 300_000,
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// Revenue summary
// ---------------------------------------------------------------------------

export interface RevenueByEntity {
  entity_id: string;
  entity_name: string;
  mtd: number;
  ytd: number;
}

const SAMPLE_REVENUE: RevenueByEntity[] = [
  { entity_id: '22222222-aaaa-aaaa-aaaa-000000000001', entity_name: 'UAE Younes', mtd: 85000, ytd: 620000 },
  { entity_id: '22222222-aaaa-aaaa-aaaa-000000000002', entity_name: 'KSA Al Jarbou', mtd: 120000, ytd: 890000 },
  { entity_id: '22222222-aaaa-aaaa-aaaa-000000000003', entity_name: 'KSA YP', mtd: 45000, ytd: 310000 },
];

async function fetchRevenueSummary(): Promise<RevenueByEntity[] | UnavailableData> {
  // No purpose-built revenue-summary endpoint and /invoices listing currently
  // 500s. Best real signal we have is setup_matter.fee_quoted aggregated by
  // billing_entity_id — represents work-in-flight value per entity. MTD =
  // matters started this month; YTD = matters started this year.
  type Matter = {
    billing_entity_id?: string;
    fee_quoted?: string | number | null;
    started_at?: string | null;
    status?: string;
  };
  type Entity = { id?: string; code?: string; legal_name_en?: string; legal_name_ar?: string };

  const [matters, entities] = await Promise.all([
    apiFetch<Matter[]>('/api/v1/setup-matters/?is_active=true', [] as Matter[]),
    apiFetch<Entity[]>('/api/v1/billing-entities', [] as Entity[]),
  ]);
  if (isUnavailable(matters) || isUnavailable(entities)) {
    return (isUnavailable(matters) ? matters : entities) as UnavailableData;
  }

  const now = new Date();
  const yearStart = new Date(now.getFullYear(), 0, 1);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  const totals = new Map<string, { mtd: number; ytd: number }>();
  for (const m of matters) {
    if (!m.billing_entity_id || m.fee_quoted == null) continue;
    if (m.status === 'cancelled') continue;
    const fee = Number(m.fee_quoted);
    if (!Number.isFinite(fee)) continue;
    const started = m.started_at ? new Date(m.started_at) : null;
    const inYear = started && started >= yearStart;
    const inMonth = started && started >= monthStart;
    const cur = totals.get(m.billing_entity_id) ?? { mtd: 0, ytd: 0 };
    if (inMonth) cur.mtd += fee;
    if (inYear) cur.ytd += fee;
    totals.set(m.billing_entity_id, cur);
  }

  return entities
    .filter((e) => e.id)
    .map((e) => {
      const t = totals.get(e.id!) ?? { mtd: 0, ytd: 0 };
      return {
        entity_id: e.id!,
        entity_name: e.legal_name_en ?? e.code ?? e.id!,
        mtd: t.mtd,
        ytd: t.ytd,
      };
    });
}

export function useRevenueSummary() {
  return useQuery({
    queryKey: ['dashboard', 'revenue-summary'],
    queryFn: fetchRevenueSummary,
    staleTime: 300_000,
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// My matters (lawyer view)
// ---------------------------------------------------------------------------

export interface MyMatter {
  id: string;
  client_name: string;
  tx_type_code: string;
  current_phase: string;
  status: string;
}

const SAMPLE_MY_MATTERS: MyMatter[] = [
  { id: '1', client_name: 'Alutec Gulf Ltd', tx_type_code: 'Post_Incorporation', current_phase: 'CR', status: 'under_process' },
  { id: '2', client_name: 'Gulf Steel Corp', tx_type_code: 'Pre_Incorporation', current_phase: 'Name_Reservation', status: 'under_process' },
];

/** Decode the current JWT once and return the auth user id. */
function getMyUserId(): string | null {
  const token = localStorage.getItem('access_token');
  if (!token) return null;
  try {
    const claims = JSON.parse(
      atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
    );
    return claims.sub ?? null;
  } catch {
    return null;
  }
}

/** Resolve current user → jy_lawyer row (id, points_ytd, display name). */
let _meLawyerCache: Promise<{
  id: string;
  user_id: string;
  display_name: string;
  display_name_ar: string;
  points_ytd: number;
} | null> | null = null;
function getMyLawyer() {
  if (_meLawyerCache) return _meLawyerCache;
  const userId = getMyUserId();
  if (!userId) return Promise.resolve(null);
  _meLawyerCache = (async () => {
    const r = await apiFetch<Array<Record<string, unknown>>>(
      `/api/v1/lawyers?user_id=${encodeURIComponent(userId)}`,
      [] as Array<Record<string, unknown>>,
    );
    if (isUnavailable(r) || r.length === 0) return null;
    const l = r[0];
    return {
      id: String(l.id ?? ''),
      user_id: String(l.user_id ?? userId),
      display_name: String(l.display_name ?? ''),
      display_name_ar: String(l.display_name_ar ?? ''),
      points_ytd: Number(l.points_ytd ?? 0),
    };
  })();
  return _meLawyerCache;
}

async function fetchMyMatters(): Promise<MyMatter[] | UnavailableData> {
  const me = await getMyLawyer();
  type Matter = {
    id: string;
    client_company_id?: string;
    transaction_type_id?: string;
    lead_lawyer_id?: string;
    status?: string;
  };
  type Phase = {
    matter_id: string;
    phase_code: string;
    phase_order: number;
    status: string;
  };
  type Company = { id: string; name: string };
  type TxType = { id: string; code: string; name_en: string; name_ar: string };

  const [matters, phases, companies, txTypes] = await Promise.all([
    apiFetch<Matter[]>('/api/v1/setup-matters/?is_active=true', [] as Matter[]),
    apiFetch<Phase[]>('/api/v1/matter-phases?limit=200', [] as Phase[]),
    apiFetch<Company[]>('/api/v1/companies?limit=200', [] as Company[]),
    apiFetch<TxType[]>('/api/v1/transaction-types?limit=50', [] as TxType[]),
  ]);
  if (isUnavailable(matters)) return matters;

  const companyMap = new Map(
    (isUnavailable(companies) ? [] : companies).map((c) => [c.id, c.name]),
  );
  const txMap = new Map(
    (isUnavailable(txTypes) ? [] : txTypes).map((t) => [t.id, t]),
  );
  const phaseList = isUnavailable(phases) ? [] : phases;

  return matters
    .filter((m) => {
      if (m.status === 'done' || m.status === 'cancelled') return false;
      // Managing partners and team leaders see all in-flight matters as "their"
      // workload; lawyers/associates see only those they lead. Without a real
      // role check available here, fall back to all matters when we can't
      // resolve a lawyer record.
      if (!me) return true;
      return m.lead_lawyer_id === me.id;
    })
    .slice(0, 10)
    .map((m) => {
      const mPhases = phaseList
        .filter((p) => p.matter_id === m.id)
        .sort((a, b) => (a.phase_order ?? 0) - (b.phase_order ?? 0));
      const active = mPhases.find((p) => p.status === 'in_progress');
      const next = mPhases.find((p) => p.status === 'not_started');
      const phaseRow = active ?? next;
      const tx = m.transaction_type_id ? txMap.get(m.transaction_type_id) : undefined;
      return {
        id: m.id,
        client_name:
          (m.client_company_id && companyMap.get(m.client_company_id)) ??
          (m.client_company_id ? `${m.client_company_id.slice(0, 8)}…` : ''),
        tx_type_code: tx?.code ?? '',
        current_phase: phaseRow?.phase_code ?? tx?.name_en ?? 'unstarted',
        status: m.status ?? '',
      };
    });
}

export function useMyMatters() {
  return useQuery({
    queryKey: ['dashboard', 'my-matters'],
    queryFn: fetchMyMatters,
    staleTime: 60_000,
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// My emails to triage
// ---------------------------------------------------------------------------

async function fetchMyEmails(): Promise<EmailSummaryItem[] | UnavailableData> {
  // No per-lawyer assignment field on inbound_email — surface the latest
  // unactioned (`classified`) emails that the user can pick up. When email
  // assignment lands, filter here.
  type Email = {
    id: string;
    subject?: string;
    from_address?: string;
    received_at?: string;
    ingested_at?: string;
    processing_status?: string;
  };
  const raw = await apiFetch<Email[]>(
    '/api/v1/email-intake/inbound-emails?limit=50',
    SAMPLE_EMAIL_SUMMARY.slice(0, 2) as unknown as Email[],
  );
  if (isUnavailable(raw)) return raw;
  return raw
    .filter((e) => e.processing_status === 'classified')
    .sort((a, b) => {
      const ta = new Date(a.received_at ?? a.ingested_at ?? 0).getTime();
      const tb = new Date(b.received_at ?? b.ingested_at ?? 0).getTime();
      return tb - ta;
    })
    .slice(0, 5)
    .map((e) => ({
      id: e.id,
      subject: e.subject ?? '',
      from_address: e.from_address ?? '',
      received_at: e.received_at ?? e.ingested_at ?? '',
      status: e.processing_status ?? '',
    }));
}

export function useMyEmails() {
  return useQuery({
    queryKey: ['dashboard', 'my-emails'],
    queryFn: fetchMyEmails,
    staleTime: 30_000,
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// My timesheet (this week)
// ---------------------------------------------------------------------------

export interface TimesheetRow {
  id: string;
  task_title: string;
  logged_at: string;
  hours: number;
  billable: boolean;
}

const SAMPLE_TIMESHEET: TimesheetRow[] = [
  { id: '1', task_title: 'CR preparation for Alutec', logged_at: '2026-04-21T10:00:00Z', hours: 2.5, billable: true },
  { id: '2', task_title: 'Client call — Jubaili Bros', logged_at: '2026-04-22T14:00:00Z', hours: 1.0, billable: true },
  { id: '3', task_title: 'Internal review meeting', logged_at: '2026-04-23T09:00:00Z', hours: 0.5, billable: false },
];

async function fetchMyTimesheet(): Promise<TimesheetRow[] | UnavailableData> {
  const me = await getMyLawyer();
  type HourLog = {
    id: string;
    lawyer_id?: string;
    task_title?: string;
    logged_at?: string;
    hours?: number | string;
    billable?: boolean;
  };
  const raw = await apiFetch<HourLog[]>(
    '/api/v1/hour-logs?limit=200',
    SAMPLE_TIMESHEET as unknown as HourLog[],
  );
  if (isUnavailable(raw)) return raw;

  // Compute current week (ISO Monday → Sunday) so the widget really shows
  // "this week's" rows, not all historical hours.
  const now = new Date();
  const dow = now.getDay() || 7;        // 1 (Mon) .. 7 (Sun)
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(now.getDate() - (dow - 1));
  const nextMonday = new Date(monday);
  nextMonday.setDate(monday.getDate() + 7);

  return raw
    .filter((r) => {
      if (me && r.lawyer_id && r.lawyer_id !== me.id) return false;
      if (!r.logged_at) return false;
      const t = new Date(r.logged_at).getTime();
      return t >= monday.getTime() && t < nextMonday.getTime();
    })
    .map((r) => ({
      id: r.id,
      task_title: r.task_title ?? '',
      logged_at: r.logged_at ?? '',
      hours: Number(r.hours ?? 0),
      billable: !!r.billable,
    }));
}

export function useMyTimesheet() {
  return useQuery({
    queryKey: ['dashboard', 'my-timesheet'],
    queryFn: fetchMyTimesheet,
    staleTime: 60_000,
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// My points
// ---------------------------------------------------------------------------

export interface MyPointsData {
  lawyer_id: string;
  lawyer_name: string;
  points: number;
  rank: number;
}

const SAMPLE_MY_POINTS: MyPointsData = {
  lawyer_id: 'me',
  lawyer_name: 'You',
  points: 870,
  rank: 4,
};

async function fetchMyPoints(): Promise<MyPointsData | UnavailableData> {
  // No bespoke leaderboard endpoint; sort the lawyer roster by points_ytd
  // and find my row + rank.
  type LawyerRow = {
    id?: string;
    user_id?: string;
    display_name?: string;
    points_ytd?: string | number;
    active?: boolean;
  };
  const me = await getMyLawyer();
  const raw = await apiFetch<LawyerRow[]>('/api/v1/lawyers?limit=50', [] as LawyerRow[]);
  if (isUnavailable(raw)) return raw;
  const ranked = raw
    .filter((l) => l.active !== false)
    .map((l) => ({
      id: String(l.id ?? ''),
      user_id: String(l.user_id ?? ''),
      display_name: String(l.display_name ?? ''),
      points: Number(l.points_ytd ?? 0),
    }))
    .sort((a, b) => b.points - a.points);
  const myIdx = me ? ranked.findIndex((r) => r.id === me.id) : -1;
  const row = myIdx >= 0 ? ranked[myIdx] : ranked[0] ?? null;
  if (!row) return SAMPLE_MY_POINTS;
  return {
    lawyer_id: row.id,
    lawyer_name: row.display_name,
    points: row.points,
    rank: (myIdx >= 0 ? myIdx : 0) + 1,
  };
}

export function useMyPoints() {
  return useQuery({
    queryKey: ['dashboard', 'my-points'],
    queryFn: fetchMyPoints,
    staleTime: 300_000,
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// My renewals
// ---------------------------------------------------------------------------

async function fetchMyRenewals(): Promise<UpcomingRenewal[] | UnavailableData> {
  // Use the working /retainers/renewals/upcoming endpoint (already filters
  // out cancelled-contract renewals after our earlier fix). Filter to
  // contracts the current lawyer leads.
  type RenewalRow = Record<string, unknown> & { contract_id?: string };
  type Contract = { id: string; lead_lawyer_id?: string };

  const me = await getMyLawyer();
  const [raw, contracts] = await Promise.all([
    apiFetch<{ items?: RenewalRow[] }>(
      '/api/v1/retainers/renewals/upcoming?days=30',
      { items: SAMPLE_RENEWALS.slice(0, 1) as unknown as RenewalRow[] },
    ),
    apiFetch<Contract[]>('/api/v1/retainer-contracts?limit=200', [] as Contract[]),
  ]);
  if (isUnavailable(raw)) return raw;
  const items: RenewalRow[] = Array.isArray(raw) ? raw : (raw.items ?? []);
  const myContractIds = new Set(
    me && !isUnavailable(contracts)
      ? contracts.filter((c) => c.lead_lawyer_id === me.id).map((c) => c.id)
      : [],
  );

  return items
    .filter((r) => {
      if (!me) return true;
      const cid = (r.contract_id as string | undefined) ?? '';
      return myContractIds.has(cid);
    })
    .map((r) => r as unknown as UpcomingRenewal);
}

export function useMyRenewals() {
  return useQuery({
    queryKey: ['dashboard', 'my-renewals'],
    queryFn: fetchMyRenewals,
    staleTime: 300_000,
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// My workload — billable vs non-billable hours by week (spec-20 E6, BRD FR-060)
// ---------------------------------------------------------------------------

export interface WorkloadEntry {
  week: string;          // ISO week label, e.g. '2026-W17'
  billable_hours: number;
  non_billable_hours: number;
}

const SAMPLE_WORKLOAD: WorkloadEntry[] = [
  { week: 'W10', billable_hours: 28, non_billable_hours: 6 },
  { week: 'W11', billable_hours: 32, non_billable_hours: 8 },
  { week: 'W12', billable_hours: 25, non_billable_hours: 5 },
  { week: 'W13', billable_hours: 30, non_billable_hours: 10 },
  { week: 'W14', billable_hours: 35, non_billable_hours: 5 },
  { week: 'W15', billable_hours: 22, non_billable_hours: 8 },
  { week: 'W16', billable_hours: 38, non_billable_hours: 2 },
  { week: 'W17', billable_hours: 18, non_billable_hours: 4 },
];

async function fetchMyWorkload(): Promise<WorkloadEntry[] | UnavailableData> {
  // No bespoke endpoint — derive 8-week histogram from /hour-logs filtered
  // to the current lawyer. Buckets by ISO week (W## label).
  const me = await getMyLawyer();
  type HourLog = {
    lawyer_id?: string;
    logged_at?: string;
    hours?: number | string;
    billable?: boolean;
  };
  const raw = await apiFetch<HourLog[]>(
    '/api/v1/hour-logs?limit=200',
    SAMPLE_WORKLOAD as unknown as HourLog[],
  );
  if (isUnavailable(raw)) return raw;

  // Build the 8 most-recent ISO weeks (Mon-anchored) including this week.
  const now = new Date();
  const dow = now.getDay() || 7;
  const thisMonday = new Date(now);
  thisMonday.setHours(0, 0, 0, 0);
  thisMonday.setDate(now.getDate() - (dow - 1));

  const weeks: Array<{ start: Date; label: string }> = [];
  for (let i = 7; i >= 0; i--) {
    const start = new Date(thisMonday);
    start.setDate(thisMonday.getDate() - i * 7);
    const isoWeek = isoWeekNumber(start);
    weeks.push({ start, label: `W${String(isoWeek).padStart(2, '0')}` });
  }
  const buckets = new Map<string, { billable: number; non_billable: number }>();
  for (const w of weeks) buckets.set(w.label, { billable: 0, non_billable: 0 });

  for (const r of raw) {
    if (me && r.lawyer_id && r.lawyer_id !== me.id) continue;
    if (!r.logged_at) continue;
    const t = new Date(r.logged_at);
    const wk = weeks.find((w) => {
      const next = new Date(w.start);
      next.setDate(w.start.getDate() + 7);
      return t >= w.start && t < next;
    });
    if (!wk) continue;
    const cur = buckets.get(wk.label)!;
    const h = Number(r.hours ?? 0);
    if (r.billable) cur.billable += h;
    else cur.non_billable += h;
  }

  return weeks.map((w) => ({
    week: w.label,
    billable_hours: buckets.get(w.label)!.billable,
    non_billable_hours: buckets.get(w.label)!.non_billable,
  }));
}

function isoWeekNumber(d: Date): number {
  const target = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const diff = (target.getTime() - firstThursday.getTime()) / 86400000;
  return 1 + Math.round((diff - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
}

export function useMyWorkload() {
  return useQuery({
    queryKey: ['dashboard', 'my-workload'],
    queryFn: fetchMyWorkload,
    staleTime: 60_000,   // E7: 60s — retainer/setup activity tier
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// My docs expiring — documents expiring within 60 days (spec-20 E6, BRD FR-114)
// ---------------------------------------------------------------------------

export interface ExpiringDoc {
  id: string;
  title: string;
  expiry_date: string;
  document_type: string;
  days_until_expiry: number;
}

const SAMPLE_EXPIRING_DOCS: ExpiringDoc[] = [
  { id: 'doc-1', title: 'Iqama — Ahmad Al-Rashid', expiry_date: '2026-05-10', document_type: 'iqama', days_until_expiry: 13 },
  { id: 'doc-2', title: 'CR Certificate — Alutec Gulf', expiry_date: '2026-05-28', document_type: 'cr_certificate', days_until_expiry: 31 },
  { id: 'doc-3', title: 'VAT Registration — Jubaili Bros', expiry_date: '2026-06-15', document_type: 'vat_certificate', days_until_expiry: 49 },
];

async function fetchMyDocsExpiring(): Promise<ExpiringDoc[] | UnavailableData> {
  // documents-service has no /expiring endpoint and no /document-expiries
  // listing. Approximate from /documents by reading any
  // metadata.expiry_date or top-level expiry_date field on the row, falling
  // back to the next 60-day window.
  type Doc = {
    id: string;
    title?: string;
    document_type?: string;
    expiry_date?: string | null;
    metadata?: Record<string, unknown> | null;
  };
  const raw = await apiFetch<Doc[]>(
    '/api/v1/documents?limit=200',
    SAMPLE_EXPIRING_DOCS as unknown as Doc[],
  );
  if (isUnavailable(raw)) return raw;

  const now = Date.now();
  const horizon = now + 60 * 24 * 60 * 60 * 1000;
  return raw
    .map((d) => {
      const meta = d.metadata ?? {};
      const expiryRaw =
        d.expiry_date ?? (meta as { expiry_date?: string }).expiry_date ?? null;
      return { d, expiry: expiryRaw ? new Date(expiryRaw).getTime() : null };
    })
    .filter((row) => row.expiry !== null && row.expiry > now && row.expiry < horizon)
    .sort((a, b) => (a.expiry! - b.expiry!))
    .slice(0, 10)
    .map(({ d, expiry }) => ({
      id: d.id,
      title: d.title ?? '',
      expiry_date: new Date(expiry!).toISOString().slice(0, 10),
      document_type: d.document_type ?? '',
      days_until_expiry: Math.max(0, Math.round((expiry! - now) / 86400000)),
    }));
}

export function useMyDocsExpiring() {
  return useQuery({
    queryKey: ['dashboard', 'my-docs-expiring'],
    queryFn: fetchMyDocsExpiring,
    staleTime: 60_000,   // E7: 60s — retainer/setup activity tier
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// Conflict review summary — manager widget (spec-20 E8, spec-15 W6.2)
// ---------------------------------------------------------------------------

export interface ConflictReviewSummary {
  pending_review_count: number;    // warned-and-proceeded, not yet partner-decided
  warned_proceeded_90d: number;    // warned-and-proceeded last 90 days
}

const SAMPLE_CONFLICT_SUMMARY: ConflictReviewSummary = {
  pending_review_count: 2,
  warned_proceeded_90d: 5,
};

async function fetchConflictReviewSummary(): Promise<ConflictReviewSummary | UnavailableData> {
  // No purpose-built /review-summary endpoint; derive from the list endpoint.
  type Row = {
    decision?: string;
    decided_by_user_id?: string | null;
    decided_at?: string | null;
    created_at?: string;
  };
  const raw = await apiFetch<{ items?: Row[] } | Row[]>(
    '/api/v1/conflict-checks?limit=200',
    [] as Row[],
  );
  if (isUnavailable(raw)) return raw;
  const items: Row[] = Array.isArray(raw) ? raw : (raw.items ?? []);
  const cutoff = Date.now() - 90 * 24 * 60 * 60 * 1000;
  let pending = 0;
  let warnedRecent = 0;
  for (const r of items) {
    if (r.decision === 'warned_and_proceeded') {
      if (!r.decided_by_user_id && !r.decided_at) pending += 1;
      const created = r.created_at ? new Date(r.created_at).getTime() : 0;
      if (created >= cutoff) warnedRecent += 1;
    }
  }
  return { pending_review_count: pending, warned_proceeded_90d: warnedRecent };
}

export function useConflictReviewSummary() {
  return useQuery({
    queryKey: ['dashboard', 'conflict-review-summary'],
    queryFn: fetchConflictReviewSummary,
    staleTime: 30_000,   // E7: 30s — SLA-adjacent, high urgency
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// Compliance dashboard summary (spec-16 W6.3 closure / Amendment A1)
// ---------------------------------------------------------------------------

export interface ComplianceSummary {
  overdue: number;
  due_this_quarter: number;
  iqama_expiring_under_30d: number;
  total_tracked: number;   // total compliance_state rows tracked
}

const SAMPLE_COMPLIANCE: ComplianceSummary = {
  overdue: 3,
  due_this_quarter: 7,
  iqama_expiring_under_30d: 2,
  total_tracked: 12,
};

async function fetchComplianceSummary(tenantId: string): Promise<ComplianceSummary | UnavailableData> {
  // The bespoke summary endpoint returns the 3 KPI counts but no row total,
  // so a tenant can hold N tracked retainers and the widget still reads "0/0/0".
  // Fetch the list count alongside to give the user a real signal.
  type Row = unknown;
  const [summary, list] = await Promise.all([
    apiFetch<Omit<ComplianceSummary, 'total_tracked'>>(
      `/api/v1/compliance-states/dashboard-summary?tenant_id=${tenantId}`,
      {
        overdue: SAMPLE_COMPLIANCE.overdue,
        due_this_quarter: SAMPLE_COMPLIANCE.due_this_quarter,
        iqama_expiring_under_30d: SAMPLE_COMPLIANCE.iqama_expiring_under_30d,
      },
    ),
    apiFetch<Row[]>('/api/v1/compliance-states?limit=200', [] as Row[]),
  ]);
  if (isUnavailable(summary)) return summary;
  const total = isUnavailable(list) ? 0 : list.length;
  return { ...summary, total_tracked: total };
}

export function useComplianceSummary(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['dashboard', 'compliance-summary', tenantId],
    queryFn: () => fetchComplianceSummary(tenantId!),
    enabled: !!tenantId,
    staleTime: 300_000,
    retry: 1,
  });
}
